import { useCallback, useRef, useState } from 'react';

/** Ignore backdrop clicks briefly after open to block ghost/synthetic clicks. */
const OVERLAY_CLICK_GUARD_MS = 400;

/**
 * Mobile nav drawer state with sticky-hover / ghost-click protection.
 *
 * Touch devices can apply :hover on the first tap and defer the real click,
 * or fire a compatibility click onto a newly mounted backdrop. Opening via
 * touchend (with preventDefault) plus a short overlay guard fixes both.
 *
 * @returns {{
 *   sidebarOpen: boolean,
 *   setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>,
 *   openSidebar: () => void,
 *   closeSidebar: () => void,
 *   onOverlayClick: () => void,
 *   menuButtonProps: {
 *     type: 'button',
 *     'aria-label': string,
 *     'aria-expanded': boolean,
 *     onTouchEnd: (e: React.TouchEvent) => void,
 *     onClick: () => void,
 *   },
 * }}
 */
export function useMobileSidebarOpen() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const ignoreOverlayClickUntilRef = useRef(0);

  const openSidebar = useCallback(() => {
    ignoreOverlayClickUntilRef.current = Date.now() + OVERLAY_CLICK_GUARD_MS;
    setSidebarOpen(true);
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const onOverlayClick = useCallback(() => {
    if (Date.now() < ignoreOverlayClickUntilRef.current) return;
    setSidebarOpen(false);
  }, []);

  const menuButtonProps = {
    type: 'button',
    'aria-label': 'Open navigation menu',
    'aria-expanded': sidebarOpen,
    onTouchEnd: (e) => {
      e.preventDefault();
      openSidebar();
    },
    onClick: openSidebar,
  };

  return {
    sidebarOpen,
    setSidebarOpen,
    openSidebar,
    closeSidebar,
    onOverlayClick,
    menuButtonProps,
  };
}

export default useMobileSidebarOpen;
